import { useState, useEffect } from 'react'
import Header from '../../components/Header'
import { useTheme } from '../../contexts/ThemeContext'
import { useAuth } from '../../contexts/AuthContext'
import { checkinsAPI, leaderboardAPI } from '../../config/api'
import CheckInTimeModal from '../../components/CheckInTimeModal'
import ConfettiEffect from '../../components/ConfettiEffect'

export default function CheckInPage() {
  const { theme } = useTheme()
  const { isAuthenticated } = useAuth()
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [isTimeModalOpen, setIsTimeModalOpen] = useState(false)
  const [showConfetti, setShowConfetti] = useState(false)
  const [checkInTime, setCheckInTime] = useState<Date | null>(null)
  const [checkInStatus, setCheckInStatus] = useState<any>(null)
  const [userStats, setUserStats] = useState({ points: 0, streak: 0 })
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    const fetchData = async () => {
      if (!isAuthenticated) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const status = await checkinsAPI.getStatus()
        setCheckInStatus(status)

        try {
          const ranking = await leaderboardAPI.getMyRanking()
          setUserStats({
            points: ranking?.points || ranking?.total_points || 0,
            streak: ranking?.streak || 0
          })
        } catch (rankingError) {
          setUserStats({ points: 0, streak: 0 })
        }
      } catch (error: any) {
        if (error.response?.status === 404) {
          setCheckInStatus({
            status: 'not_checked_in',
            message: 'Chưa điểm danh hôm nay'
          })
        }
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [isAuthenticated])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedFile(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setPreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleCheckIn = async () => {
    if (!selectedFile || !isAuthenticated) {
      return
    }

    try {
      setSubmitting(true)
      const now = new Date()
      setCheckInTime(now)
      
      await checkinsAPI.createCheckin({
        image: selectedFile,
        notes: `Điểm danh ngày ${now.toLocaleDateString('vi-VN')}`
      })

      try {
        const status = await checkinsAPI.getStatus()
        setCheckInStatus(status)
      } catch (error) {
        // Error refreshing check-in status
      }

      setShowConfetti(true)
      setTimeout(() => {
        setIsTimeModalOpen(true)
      }, 300)

      try {
        const ranking = await leaderboardAPI.getMyRanking()
        setUserStats({
          points: ranking?.points || ranking?.total_points || 0,
          streak: ranking?.streak || 0
        })
      } catch (error) {
        // Error refreshing ranking
      }

      setSelectedFile(null)
      setPreview(null)
    } catch (error: any) {
      // Error checking in
      
      if (error.response?.status === 400) {
        try {
          const status = await checkinsAPI.getStatus()
          setCheckInStatus(status)
        } catch (refreshError) {
          // Error refreshing status
        }
      }
    } finally {
      setSubmitting(false)
    }
  }

  const getCurrentDate = () => {
    const now = new Date()
    const days = ['Chủ Nhật', 'Thứ Hai', 'Thứ Ba', 'Thứ Tư', 'Thứ Năm', 'Thứ Sáu', 'Thứ Bảy']
    const months = ['tháng 1', 'tháng 2', 'tháng 3', 'tháng 4', 'tháng 5', 'tháng 6', 
                   'tháng 7', 'tháng 8', 'tháng 9', 'tháng 10', 'tháng 11', 'tháng 12']
    
    const dayName = days[now.getDay()]
    const day = now.getDate()
    const month = months[now.getMonth()]
    const year = now.getFullYear()
    
    return `${dayName}, ${day} ${month} ${year}`
  }

  return (
    <main className={`min-h-screen ${theme === 'dark' ? 'bg-black' : 'bg-gray-200'}`}>
      <Header />

      <section className="py-8 md:py-12 px-4">
        <div className="container mx-auto max-w-2xl">
          <div className="text-center mb-6">
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${theme === 'dark' ? 'bg-gold-500/20 border border-gold-500/50' : 'bg-gold-400/20 border border-gold-400/50'}`}>
              <i className={`pi pi-calendar text-lg ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}></i>
              <span className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                {getCurrentDate()}
              </span>
            </div>
          </div>

          <div className="text-center mb-8">
            <h1 className={`text-3xl md:text-4xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>ĐIỂM DANH</span>{' '}
              <span className="text-gradient">HÔM NAY</span>
            </h1>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className={`rounded-xl p-5 md:p-6 text-center ${theme === 'dark' ? 'bg-black/80 border border-gold-500/30' : 'bg-white border border-gold-400/30'}`}>
              {loading ? (
                <div className="animate-pulse">
                  <div className="h-12 bg-gray-700 rounded mb-3"></div>
                  <div className="h-4 bg-gray-700 rounded"></div>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-center gap-3 mb-3">
                    <i className={`pi pi-trophy text-3xl md:text-4xl ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}></i>
                    <span className={`text-3xl md:text-4xl font-bold ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}>
                      {userStats.points}
                    </span>
                  </div>
                  <div className={`text-sm md:text-base font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Tổng Điểm
                  </div>
                </>
              )}
            </div>

            <div className={`rounded-xl p-5 md:p-6 text-center ${theme === 'dark' ? 'bg-black/80 border border-gold-500/30' : 'bg-white border border-gold-400/30'}`}>
              {loading ? (
                <div className="animate-pulse">
                  <div className="h-12 bg-gray-700 rounded mb-3"></div>
                  <div className="h-4 bg-gray-700 rounded"></div>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-center gap-3 mb-3">
                    <i className={`pi pi-bolt text-3xl md:text-4xl ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}></i>
                    <span className={`text-3xl md:text-4xl font-bold ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}>
                      {userStats.streak}
                    </span>
                  </div>
                  <div className={`text-sm md:text-base font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Chuỗi Ngày
                  </div>
                </>
              )}
            </div>
          </div>

          <div className={`rounded-2xl p-6 md:p-8 ${theme === 'dark' ? 'bg-black/80 border border-gold-500/30' : 'bg-white border border-gold-400/30'}`}>
            <h2 className={`text-xl md:text-2xl font-bold mb-2 text-center ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              Sẵn Sàng Điểm Danh?
            </h2>
            <p className={`text-sm md:text-base text-center mb-6 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              Tải lên ảnh để xác nhận sự có mặt của bạn
            </p>

            <div className={`border-2 border-dashed rounded-xl p-8 md:p-12 text-center mb-6 transition ${theme === 'dark' ? 'border-gold-500/50 hover:border-gold-500' : 'border-gold-400/50 hover:border-gold-400'} ${preview ? 'border-solid' : ''}`}>
              <input
                type="file"
                accept="image/*,.heic,.heif,.webp"
                onChange={handleFileChange}
                className="hidden"
                id="checkin-file-upload"
              />
              <label
                htmlFor="checkin-file-upload"
                className="cursor-pointer block"
              >
                {preview ? (
                  <div className="space-y-3">
                    <img
                      src={preview}
                      alt="Preview"
                      className="max-w-full h-auto max-h-64 mx-auto rounded-lg"
                    />
                    <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      {selectedFile?.name}
                    </p>
                    <p className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                      Click để chọn ảnh khác
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="mb-4">
                      <i className={`pi pi-cloud-upload text-5xl ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}></i>
                    </div>
                    <p className={`text-sm md:text-base font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      Nhấn để tải ảnh lên
                    </p>
                    <p className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'}`}>
                      Tối đa 10MB
                    </p>
                  </div>
                )}
              </label>
            </div>

            {checkInStatus?.status === 'checked_in' ? (
              <div className={`w-full py-4 px-6 bg-green-500/20 border-2 border-green-500 text-green-400 font-bold text-base md:text-lg rounded-lg text-center`}>
                <i className="pi pi-check-circle text-lg mr-2"></i>
                <span>ĐÃ ĐIỂM DANH HÔM NAY</span>
              </div>
            ) : (
              <button
                onClick={handleCheckIn}
                disabled={!selectedFile || submitting || !isAuthenticated}
                className={`w-full py-4 px-6 bg-gold-gradient text-white font-bold text-base md:text-lg rounded-lg hover:shadow-lg hover:shadow-gold-500/50 disabled:opacity-50 disabled:cursor-not-allowed transition transform hover:scale-105 flex items-center justify-center gap-2`}
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div>
                    <span>Đang xử lý...</span>
                  </>
                ) : (
                  <>
                    <i className="pi pi-check text-lg"></i>
                    <span>ĐIỂM DANH HÔM NAY (+1)</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </section>

      <ConfettiEffect 
        trigger={showConfetti}
        onComplete={() => setShowConfetti(false)}
      />

      <CheckInTimeModal 
        isOpen={isTimeModalOpen} 
        onClose={() => setIsTimeModalOpen(false)}
        checkInTime={checkInTime || undefined}
      />
    </main>
  )
}

